import { Box, BoxProps } from "@chakra-ui/react";
import { CONTENT_FULL_HEIGHT } from "components/Layout/constants";
import ChakraReactTable, {
  ComponentProps,
  Props as ChakraReactTableProps,
} from "components/Shared/ChakraReactTable";
import ScrollContainer, {
  ScrollContainerProps,
} from "components/Shared/ChakraReactTable/ScrollContainer";
import StandardTableControls, {
  Props as StandardTableControlsProps,
} from "components/Shared/ChakraReactTable/StandardTableControls";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import { useChakraReactTableProps } from "components/Shared/ChakraReactTable/utils";

export function FullPageTableControls<D extends object>(
  props: StandardTableControlsProps<D>,
) {
  return (
    <StandardTableControls
      flex="0 0 auto"
      overflowX="auto"
      p={4}
      borderTopWidth={1}
      {...props}
    />
  );
}

export function useFullPageTableProps<D extends object>(
  extraProps: Partial<ComponentProps<D>>[] = [],
): Partial<ComponentProps<D>> {
  return useChakraReactTableProps([
    useStickyHeader(),
    {
      getColumnProps: (column, { isFirst, isLast }) => {
        if (isFirst) return { pl: 4 };
        if (isLast) return { pr: 4 };
        return {};
      },
      noRowsPlaceholderProps: {
        textAlign: "center",
        color: "gray.500",
        py: 10,
        px: 5,
      },
    },
    ...extraProps,
  ]);
}

export type FullPageTableExtraProps<D extends object> = Pick<
  StandardTableControlsProps<D>,
  | "totalCount"
  | "resultTypeLabel"
  | "isExporting"
  | "isPaginationEnabled"
  | "onExport"
  | "onRefresh"
> & {
  scrollContainerProps?: ScrollContainerProps;
  tableContainerProps?: BoxProps;
  tableControlsFooterProps?: BoxProps;
};

export type FullPageTableProps<D extends object> = ChakraReactTableProps<D> &
  FullPageTableExtraProps<D>;

export default function FullPageTable<D extends object>({
  totalCount,
  resultTypeLabel,
  isExporting,
  isPaginationEnabled,
  onExport,
  onRefresh,
  scrollContainerProps,
  tableContainerProps,
  tableControlsFooterProps,
  ...tableProps
}: FullPageTableProps<D>) {
  const { tableInstance, isLoading } = tableProps;

  return (
    <>
      <Box flex="1 1 100%" minH={0} {...tableContainerProps}>
        <ScrollContainer {...scrollContainerProps}>
          <ChakraReactTable {...tableProps} />
        </ScrollContainer>
      </Box>

      <FullPageTableControls
        {...{
          isExporting,
          isPaginationEnabled,
          isLoading,
          onExport,
          onRefresh,
          resultTypeLabel,
          tableInstance,
          totalCount,
        }}
        {...tableControlsFooterProps}
      />
    </>
  );
}

export const FREESTANDING_TABLE_CONTAINER_PROPS: Pick<
  FullPageTableProps<object>,
  "scrollContainerProps" | "tableControlsFooterProps"
> = {
  scrollContainerProps: {
    borderWidth: 1,
    borderTopRadius: ".25rem",
    minHeight: "15rem",
    maxHeight: `calc(${CONTENT_FULL_HEIGHT} - 15.5rem)`,
  },
  tableControlsFooterProps: { px: 0 },
};
